export type ImageFit = "cover" | "contain" | "crop" | "scale-down" | "pad";
export type ImageFormat = "auto" | "webp" | "avif" | "jpeg" | "png";
export type ImageGravity =
  | "auto"
  | "left"
  | "right"
  | "top"
  | "bottom"
  | "face"
  | { x: number; y: number };

export interface ImageTransformOptions {
  width?: number;
  height?: number;
  fit?: ImageFit;
  format?: ImageFormat;
  quality?: number;
  dpr?: number;
  gravity?: ImageGravity;
  removeBackground?: boolean;
}

export interface ImageUrlConfig {
  /** Base URL of the zone with CF Image Transformations enabled. No trailing slash. */
  baseUrl: string;
}

/** Default width ladder for responsive product images. */
export const DEFAULT_WIDTH_LADDER = [240, 480, 960, 1440] as const;

function serializeGravity(gravity: ImageGravity): string {
  if (typeof gravity === "string") {
    return gravity;
  }
  return `${gravity.x}x${gravity.y}`;
}

function buildOptionsString(opts: ImageTransformOptions): string {
  const format: ImageFormat = opts.format ?? "auto";
  const quality: number = opts.quality ?? 85;

  const parts: string[] = [];

  // Stable order: width, height, fit, format, quality, dpr, gravity, segment
  if (opts.width !== undefined) {
    parts.push(`width=${opts.width}`);
  }
  if (opts.height !== undefined) {
    parts.push(`height=${opts.height}`);
  }
  if (opts.fit !== undefined) {
    parts.push(`fit=${opts.fit}`);
  }
  parts.push(`format=${format}`);
  parts.push(`quality=${quality}`);
  if (opts.dpr !== undefined) {
    parts.push(`dpr=${opts.dpr}`);
  }
  if (opts.gravity !== undefined) {
    parts.push(`gravity=${serializeGravity(opts.gravity)}`);
  }
  if (opts.removeBackground === true) {
    parts.push(`segment=foreground`);
  }

  return parts.join(",");
}

function encodeObjectKeyPath(key: string): string {
  return key
    .replace(/^\/+/, "")
    .split("/")
    .map(encodeURIComponent)
    .join("/");
}

function stripTrailingSlashes(s: string): string {
  return s.replace(/\/+$/, "");
}

/**
 * Object-key prefixes that live in the *private* R2 bucket and are only ever
 * reachable through a backend-minted signed URL. The media zone fronts the
 * public bucket, so building a transformation URL for one of these yields a
 * plausible-looking link that 404s — see `assertPublicObjectKey`.
 */
const PRIVATE_KEY_PATTERNS = [
  /(^|\/)orders\/payment-proofs\//,
  /^admin\/subscriptions\/payments\//,
] as const;

/** True when `objectKey` belongs to the private bucket. */
export function isPrivateObjectKey(objectKey: string): boolean {
  const key = objectKey.replace(/^\/+/, "");
  return PRIVATE_KEY_PATTERNS.some((pattern) => pattern.test(key));
}

function assertPublicObjectKey(objectKey: string): void {
  if (isPrivateObjectKey(objectKey)) {
    throw new Error(
      `imageUrl() was given a private-bucket object key (${objectKey}). ` +
        "Private objects are not served by the media zone — fetch a signed URL " +
        "from the backend instead (e.g. GET /seller/orders/:id/proof).",
    );
  }
}

/**
 * Builds a Cloudflare Image Transformations URL for an R2 object.
 *
 * Throws for private-bucket keys: the resulting URL would resolve against the
 * public media zone and render as a broken image rather than failing.
 *
 * Example output:
 *   https://media.example.com/cdn-cgi/image/width=480,format=auto,quality=85/products/abc.jpg
 */
export function imageUrl(
  objectKey: string,
  opts: ImageTransformOptions,
  config: ImageUrlConfig,
): string {
  assertPublicObjectKey(objectKey);
  const base = stripTrailingSlashes(config.baseUrl);
  const key = encodeObjectKeyPath(objectKey);
  const optionsString = buildOptionsString(opts);
  return `${base}/cdn-cgi/image/${optionsString}/${key}`;
}

/**
 * Builds a `srcset` string from a list of widths. Other options carry through.
 *
 * Example output:
 *   ".../cdn-cgi/image/width=240,format=auto/k 240w, .../cdn-cgi/image/width=480,format=auto/k 480w"
 */
export function imageSrcset(
  objectKey: string,
  widths: number[],
  opts: Omit<ImageTransformOptions, "width">,
  config: ImageUrlConfig,
): string {
  return widths
    .map((width) => {
      const url = imageUrl(objectKey, { ...opts, width }, config);
      return `${url} ${width}w`;
    })
    .join(", ");
}
